import React, { useState, useEffect } from "react";
import moment from "moment";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useUsersManagementApi } from "src/_common/hooks/actions";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useHistory } from "react-router-dom";
import { INTERVIEW_STATUS } from "src/_config";
import ScheduleInterviewModal from "src/components/job-details/Interview/ScheduleInterviewModal";

interface InterviewSchedule {
  approved: {
    schedules: any[];
    total: number;
  };
  pending: {
    schedules: any[];
    total: number;
  };
}

interface Member {
  user_id: number;
  id: string;
  name: string;
  email: string;
  assign_workspace: string;
  role_id: number;
  employee_code: string;
  designation: string;
  profile_photo_url?: string;
  status: boolean;
}

export default function UpcomingInterviewSection() {
  const { t } = useTranslation("dashboard");
  const toast = useToaster();
  const interviewApi = useInterviewApi();
  const userDetails = useAppUserDetailsSelector();
  const { callGetUsers } = useUsersManagementApi();
  const history = useHistory();

  const [meetings, setMeetings] = useState<InterviewSchedule>({
    approved: { schedules: [], total: 0 },
    pending: { schedules: [], total: 0 },
  });
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [meetingToReschedule, setMeetingToReschedule] = useState<any>(null);

  // Load members on component mount
  useEffect(() => {
    if (userDetails?.user_type !== "member") {
      loadMembers();
    }
  }, []);

  // Load interview schedule on component mount
  useEffect(() => {
    loadInterviewSchedule();
  }, []);

  // Load interview schedule when selected member changes
  useEffect(() => {
    if (selectedMember) {
      loadInterviewSchedule(selectedMember.user_id);
    }
  }, [selectedMember]);

  // Function to load members list
  const loadMembers = () => {
    setLoadingMembers(true);
    const params = {
      limit: 100,
      offset: 0,
    };

    callGetUsers(
      params,
      (message: string, data: any) => {
        console.log("Members loaded successfully:", data);
        if (data && data.members) {
          setMembers(data.members);
          // Select first member by default (filter out members without names)
          const membersWithNames = data.members.filter((m: any) => m.name && m.name.trim());
          if (membersWithNames.length > 0) {
            setSelectedMember(membersWithNames[0]);
          }
        }
        setLoadingMembers(false);
      },
      (error: string) => {
        console.error("Error loading members:", error);
        toast.error("Failed to load members");
        setLoadingMembers(false);
      }
    );
  };

  // Function to load interview schedule from API
  const loadInterviewSchedule = (member_id?: any) => {
    setLoadingSchedule(true);
    interviewApi.callGetSchedule(
      { ...(member_id ? { member_id } : {}) },
      (msg: any, response: any) => {
        console.log("Interview schedule API response:", response);
        setMeetings(
          response?.interview_schedule || {
            approved: { schedules: [], total: 0 },
            pending: { schedules: [], total: 0 },
          }
        );
        setLoadingSchedule(false);
      },
      (error: any) => {
        console.error("Failed to load interview schedule:", error);
        setLoadingSchedule(false);
      }
    );
  };

  const handleInterviewClick = (meeting: any) => {
    // Navigate to interview schedule page or show details
    history.push("/interview-schedule");
  };

  const handleRescheduleCancelInterview = (meeting: any, status: number) => {
    interviewApi.callRescheduleOrCancel(
      {
        schedule_id: meeting?.id,
        interview_status: status,
      },
      (message: string, resp: any) => {
        toast.success(
          status === INTERVIEW_STATUS.CANCEL
            ? t("dashboard.upcomingInterviews.interviewCancelledSuccess")
            : t("dashboard.upcomingInterviews.interviewRescheduledSuccess")
        );
        // Refresh the interview schedule
        loadInterviewSchedule(selectedMember?.user_id);
      },
      (error: string) => {
        toast.error(error || t("dashboard.upcomingInterviews.failedToUpdateInterview"));
        loadInterviewSchedule(selectedMember?.user_id);
      }
    );
  };

  const handleRescheduleInterview = (meeting: any) => {
    setMeetingToReschedule(meeting);
    setIsScheduleModalOpen(true);
  };

  const handleCancelInterview = (meeting: any) => {
    handleRescheduleCancelInterview(meeting, INTERVIEW_STATUS.CANCEL);
  };

  const handleStartInterview = (meeting: any) => {
    if (meeting?.interview_url) {
      window.open(meeting.interview_url, "_blank");
    }
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setMeetingToReschedule(null);
  };

  const handleScheduleConfirm = (scheduleData: any) => {
    console.log("Interview rescheduled with data:", scheduleData);
    // Refresh the interview schedule
    loadInterviewSchedule(selectedMember?.user_id);
    handleCloseScheduleModal();
  };

  // Check if interview is canceled (same logic as Interview tab)
  const isInterviewCanceled = (meeting: any) => {
    return meeting?.interview_status == 0;
  };

  // Check if Start button should be shown (same logic as Interview tab)
  const shouldShowStartButton = (meeting: any) => {
    // Show start button only when:
    // 1. Interview is not canceled
    // 2. reminder has been sent (reminder_sent == 1)
    // 3. interview_url exists
    return !isInterviewCanceled(meeting) && meeting?.reminder_sent == 1 && meeting?.interview_url;
  };

  // Check if Reschedule/Cancel buttons should be shown (same logic as Interview tab)
  const shouldShowRescheduleCancelButtons = (meeting: any) => {
    // Show reschedule/cancel buttons when:
    // 1. Interview is not canceled
    // 2. Interview is scheduled but reminder not sent yet OR no interview_url exists
    return !isInterviewCanceled(meeting) && !shouldShowStartButton(meeting);
  };

  return (
    <div className="upcoming-interview-section">
      <div className="section-header">
        <h2 className="section-title">{t("dashboard.upcomingInterviews.title")}</h2>
        {userDetails?.user_type !== "member" && (
          <div className="member-select-container">
            <label htmlFor="member-select" style={{ fontWeight: "500", marginRight: "10px" }}>
              {t("dashboard.upcomingInterviews.memberLabel")}
            </label>
            <select
              id="member-select"
              className="basic-select"
              style={{ minWidth: "200px" }}
              value={selectedMember?.user_id || ""}
              onChange={(e) => {
                const memberId = parseInt(e.target.value);
                const member = members.find((m) => m.user_id === memberId);
                setSelectedMember(member || null);
              }}
              disabled={loadingMembers}
            >
              {loadingMembers ? (
                <option>{t("dashboard.upcomingInterviews.loadingMembers")}</option>
              ) : members.length === 0 ? (
                <option>{t("dashboard.upcomingInterviews.noMembers")}</option>
              ) : (
                members
                  .filter((member) => member.name && member.name.trim())
                  .map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.designation ? `${member.name} - ${member.designation}` : member.name}
                    </option>
                  ))
              )}
            </select>
          </div>
        )}
      </div>

      <div className="interview-content">
        {loadingSchedule ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("dashboard.upcomingInterviews.loading")}</p>
          </div>
        ) : (
          <>
            {/* Upcoming Approved Meetings */}
            {meetings?.approved?.schedules.length > 0 && (
              <div className="interview-list">
                <div className="upcoming-interviews-wrapper">
                  {meetings.approved.schedules.slice(0, 5).map((meeting, index) => {
                    const meetingDateTime = meeting.interview_schedule
                      ? moment.utc(meeting.interview_schedule).local()
                      : null;
                    return (
                      <div key={meeting.id || meeting.screening_id || index} className="upcoming-interviews-row">
                        <div className="interview-upcomming-dashboard-wrap">
                          <div className="interview-upcomming-dashboard-cont">
                            {meeting.screening?.job_details?.job_title && (
                              <h3>
                                <strong>{meeting.screening.job_details.job_title}</strong>
                              </h3>
                            )}
                            <h3>
                              <strong>{t("dashboard.upcomingInterviews.candidateLabel")}</strong>{" "}
                              {meeting.screening?.jobseeker_details?.name ||
                                t("dashboard.upcomingInterviews.notAvailable")}
                            </h3>
                            <div className="d-flex upcoming-interviews-datetime">
                              <h5>
                                <i className="interviews-date-icon"></i>{" "}
                                {meetingDateTime?.isValid()
                                  ? meetingDateTime.format("ddd, MMM DD, YYYY")
                                  : t("dashboard.upcomingInterviews.invalidDate")}
                              </h5>
                              <h5>
                                <i className="interviews-time-icon"></i>{" "}
                                {meetingDateTime?.isValid()
                                  ? meetingDateTime.format("h:mm A")
                                  : t("dashboard.upcomingInterviews.invalidTime")}
                              </h5>
                            </div>
                          </div>
                          <div className="upcoming-interviews-btns">
                            {!isInterviewCanceled(meeting) ? (
                              // Show Start/Reschedule/Cancel buttons when interview is not canceled
                              <>
                                {shouldShowStartButton(meeting) && (
                                  <button
                                    className="btn btn-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartInterview(meeting);
                                    }}
                                  >
                                    <i className="interviews-start-icon"></i> {t("dashboard.upcomingInterviews.start")}
                                  </button>
                                )}
                                {shouldShowRescheduleCancelButtons(meeting) && (
                                  <>
                                    <button
                                      className="btn btn-secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRescheduleInterview(meeting);
                                      }}
                                    >
                                      <i className="interviews-reschedule-icon"></i> Reschedule
                                    </button>
                                    <button
                                      className="btn btn-danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelInterview(meeting);
                                      }}
                                    >
                                      <i className="interviews-cancel-icon"></i> Cancel
                                    </button>
                                  </>
                                )}
                              </>
                            ) : (
                              // Show disabled Cancelled button when interview is canceled
                              <button className="btn btn-danger" disabled>
                                {t("dashboard.upcomingInterviews.cancelled")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {meetings.approved.schedules.length > 5 && (
                  <div className="view-all-link">
                    <button onClick={() => history.push("/interview-schedule")} className="btn-link">
                      {t("dashboard.upcomingInterviews.viewAll") || "View All"} ({meetings.approved.total})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* No Interviews Message */}
            {meetings?.approved?.schedules.length === 0 && (
              <div className="no-interviews">
                <i className="fa fa-calendar-times"></i>
                <p>{t("dashboard.upcomingInterviews.noInterviews")}</p>
                <button onClick={() => history.push("/interview-schedule")} className="btn-preview">
                  {t("dashboard.upcomingInterviews.scheduleInterview")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        candidateName={meetingToReschedule?.screening?.jobseeker_details?.name || ""}
        candidateId={meetingToReschedule?.screening_id || ""}
        jobId={meetingToReschedule?.job_id || ""}
        onSchedule={handleScheduleConfirm}
      />
    </div>
  );
}
